import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { fluid } from '../styles/motion'; // [MOTION-A 2026-08-20] [FLUID-3 2026-10-12]
import { usePresence } from '../hooks/usePresence'; // [FLUID-3 2026-10-12]
import { fluidDialog, fluidScrim, fluidCascade } from './Fluid'; // [FLUID-3 2026-10-12]
// [LIQUID-GLASS 2026-11-02, user] the window, boxes and buttons use the shared liquid glass recipe, like Edit Mapping
import {
  lgSurface, lgControl, lgPress, lgSunken, lgSquircle, lgHair, lgGreen,
  lgButtonPrimary, lgButtonSecondary, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass';
import { createPayment, CreatePaymentInput } from '../services/api';
import { Payment } from '../types';
import GlassSelect from './GlassSelect';
import SingleDateCalendar from './SingleDateCalendar'; // [DATE-STYLE 2026-09-29]

// [AUTO-REFRESH 2026-08-03] `data-reload-block` marks "a dialog is open" for the auto-reload
// guard (services/reloadGuard), so the update banner can never reload the tab out from under
// an unsaved form. Applied via .attrs so every instance carries it automatically.
// [MOTION-A 2026-08-20] The parent keeps us mounted via usePresence while the close plays.
// [FLUID-3 2026-10-12, user] `$open` drives the fluid recipe (components/Fluid.tsx): the shade fades in,
// fades out quicker, and stops taking clicks the moment a close begins. Was a fadeIn / apFadeOut keyframe pair.
const Overlay = styled.div.attrs<{ 'data-reload-block'?: string; $open: boolean }>({ 'data-reload-block': '' })`
  position: fixed;
  inset: 0;
  /* [LIQUID-GLASS 2026-11-02, user] The shade behind the window: a little darker, a softer blur, almost no colour boost. */
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.38)' : 'rgba(20, 30, 25, 0.14)'};
  backdrop-filter: blur(12px) saturate(115%);
  -webkit-backdrop-filter: blur(12px) saturate(115%);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  ${(p) => fluidScrim(p.$open)}
`;

/* [LIQUID-GLASS 2026-11-02, user] "the pop-up that comes after clicking on the add button … make the theme there also,
   just like … the app alerts": the same frosted glass as Edit Mapping (styles/liquidGlass.ts) — softer blur, smooth
   24 px corners, a light line on the top edge, a soft reflection on the bottom one. The window itself no longer
   scrolls: only the fields do (Form / Body below), so the glass rim never scrolls away and the blur never sits on a
   scrolling box. The old dark shade along the bottom is gone (the rim took its ::before). */
const Modal = styled.div<{ $open: boolean }>`
  position: relative;
  ${lgSurface(38)}
  ${lgSquircle(24)}
  width: 100%;
  max-width: 560px;
  max-height: calc(92vh / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* [FLUID-3 2026-10-12, user] Rises 12px and grows from 0.95 on the spring (readable after ~0.1 s, open by
     0.36 s), closes in 0.18 s; the header, the fields and the buttons rise in one after another. Replaces
     the popIn / apPopOut keyframes. */
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open ? fluidCascade('form > *', 0, 20, 3) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}

  & > * { position: relative; z-index: 1; }
`;

/* [LIQUID-GLASS 2026-11-02] The form fills the window; its header and buttons stay put and the fields (Body) scroll. */
const Form = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  flex-shrink: 0;
  border-bottom: 1px solid ${lgHair};
  background: transparent;
`;

const Title = styled.h2`
  font-size: 15px;
  font-weight: 700;
  color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.green600};
  text-shadow: ${(p) => p.theme.colorScheme === 'dark' ? '0 1px 2px rgba(0,0,0,0.45)' : 'none'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.2px;
  /* [LIQUID-GLASS 2026-11-02] A small glossy green capsule, like Edit Mapping's. */
  &::before {
    content: '';
    width: 4px;
    height: 16px;
    background: ${(p) => p.theme.colorScheme === 'dark'
      ? 'linear-gradient(180deg, #8ff7c6, #4acd8b)'
      : 'linear-gradient(180deg, #34d39e, #1a7f64)'};
    border-radius: 999px;
    box-shadow: 0 0 10px color-mix(in srgb, ${lgGreen} 40%, transparent);
  }
`;

/* [LIQUID-GLASS 2026-11-02] A round glass button that squishes and glows where it is pressed. */
const CloseBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgControl}
  ${lgPress}
  position: relative;
  border: none;
  color: ${(p) => p.theme.text};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  & > * { position: relative; z-index: 1; }
`;

const Body = styled.div`
  padding: 18px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  /* [LIQUID-GLASS 2026-11-02] the part that scrolls when the window is taller than the screen */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  @media (max-width: 540px) { grid-template-columns: 1fr; }
`;

const Field = styled.label<{ $full?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.theme.muted};
  ${(p) => p.$full && 'grid-column: 1 / -1;'}
`;

const FieldLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Required = styled.span`
  color: #ef4444;
  font-weight: 700;
`;

/* [DATE-STYLE 2026-09-29, user] The app's own calendar in place of the browser's mm/dd/yyyy box. Its
   button is already the Input's 38px / 10px-corner cell; this only makes it fill the field's width. */
const DateCell = styled.div`
  display: grid;
  & > button {
    width: 100%;
    /* [LIQUID-GLASS 2026-11-02] the calendar's button drawn as the window's other pressed-in boxes */
    border-radius: 12px;
    border: 1px solid transparent;
    background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)'};
    box-shadow: ${(p) => p.theme.colorScheme === 'dark'
      ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
      : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)'};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
`;

/* [LIQUID-GLASS 2026-11-02, user] Pressed into the glass, 12 px corners; the box you type in gets a soft green ring and
   glow (lgSunken). Was a flat 10 px box with its own 8 px blur. */
const Input = styled.input`
  height: 38px;
  padding: 0 12px;
  border-radius: 12px;
  ${lgSunken}
  color: ${(p) => p.theme.text};
  font-size: 13px;
  font-weight: 500;
  color-scheme: ${(p) => (p.theme as any).colorScheme || 'light'};
  transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1);

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: ${(p) => p.theme.colorScheme === 'dark' ? 'invert(0.85)' : 'none'};
  }
`;

/* Match the Input box look — height, padding, radius, focus ring identical to <Input>.
   [LIQUID-GLASS 2026-11-02] Now the pressed-in glass box; its list opens as frosted glass (liquidList on the element). */
const Select = styled(GlassSelect)`
  && {
    min-width: 100%;
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    height: 38px;
    padding: 0 32px 0 12px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;
    justify-content: flex-start;
    border: 1px solid transparent;
    background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)'};
    background-image: none;
    box-shadow: ${(p) => p.theme.colorScheme === 'dark'
      ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
      : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)'};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    color: ${(p) => p.theme.colorScheme === 'dark' ? '#ffffff' : p.theme.text};
    transition: border-color 120ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 120ms cubic-bezier(0.4, 0, 0.2, 1), background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  &&[aria-expanded="true"],
  &&:focus,
  &&:focus-visible {
    outline: none;
    border-color: color-mix(in srgb, ${lgGreen} 70%, transparent);
    box-shadow: ${(p) => p.theme.colorScheme === 'dark'
      ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
      : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)'},
      0 0 0 4px color-mix(in srgb, ${lgGreen} 16%, transparent),
      0 0 18px color-mix(in srgb, ${lgGreen} 24%, transparent);
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  flex-shrink: 0;
  border-top: 1px solid ${lgHair};
  background: transparent;
`;

// [BUTTON-STYLE 2026-09-30] $primary = the green primary, otherwise the secondary (Cancel).
// [LIQUID-GLASS 2026-11-02] Glass capsules (squish + press light).
const Btn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $primary?: boolean }>`
  ${(p) => (p.$primary ? lgButtonPrimary : lgButtonSecondary)}
`;

const ErrorText = styled.div`
  grid-column: 1 / -1;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.35);
  color: #ef4444;
  border-radius: 12px;
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
`;

interface Props {
  accounts: string[];
  onClose: () => void;
  onCreated: (row: Payment) => void;
  // [MOTION-A 2026-08-20] True while the parent holds us mounted for the exit animation.
  closing?: boolean;
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// [ADD-PAYMENT 2026-07-07] Pre-fill the Charge ID with a unique IST timestamp so a manual
// payment doesn't need one typed by hand. Format: DD_MM_YYYY_H_MM_SS in Asia/Kolkata
// (e.g. "07_07_2026_6_31_12") — day/month/minute/second zero-padded, hour on the 12-hour
// wall clock without a leading zero (matches how the user reads it). Seconds are included on
// purpose: charge_id + account_name is the payments unique key, so a same-minute value could
// silently overwrite an earlier row. Still fully editable.
function defaultChargeId(): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value || '';
  const hour = String(Number(get('hour'))); // 12-hour, no leading zero (matches "6", "12")
  return `${get('day')}_${get('month')}_${get('year')}_${hour}_${get('minute')}_${get('second')}`;
}

const AddPaymentModal: React.FC<Props> = ({ accounts, onClose, onCreated, closing = false }) => {
  // Lazy initializer → todayYmd()/defaultChargeId() are captured ONCE when the modal opens
  // (it mounts fresh on each open), so the Charge ID reflects the moment the user clicked Add.
  const [form, setForm] = useState<CreatePaymentInput>(() => ({
    payment_date: todayYmd(),
    charge_id: defaultChargeId(),
    amount: '',
    account_name: accounts[0] ?? '',
    email: '',
    website: '',
    client_name: '',
    description: '',
    object_type: '',
    invoice_number: '',
    status: '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // [FLUID-3 2026-10-12] Open one frame after mount (so the spring has a closed state to start from) until the
  // parent starts the close.
  const open = usePresence(!closing, fluid.dialogOutMs).entered;

  const set = <K extends keyof CreatePaymentInput>(key: K, val: CreatePaymentInput[K]) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side required-field check
    if (!form.payment_date) return setError('Payment date is required');
    if (!form.charge_id.trim()) return setError('Charge ID is required');
    if (form.amount === '' || form.amount == null) return setError('Amount is required');
    const amt = parseFloat(String(form.amount));
    if (isNaN(amt) || amt <= 0) return setError('Amount must be a positive number');
    if (!form.account_name) return setError('Account is required');

    setSaving(true);
    try {
      const cleaned: CreatePaymentInput = {
        payment_date: form.payment_date,
        charge_id: form.charge_id.trim(),
        amount: amt,
        account_name: form.account_name,
        email: form.email?.trim() || undefined,
        website: form.website?.trim() || undefined,
        client_name: form.client_name?.trim() || undefined,
        description: form.description?.trim() || undefined,
        object_type: form.object_type?.trim() || undefined,
        invoice_number: form.invoice_number?.trim() || undefined,
        status: form.status?.trim() || undefined,
      };
      const row = await createPayment(cleaned);
      onCreated(row);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to create payment');
    } finally {
      setSaving(false);
    }
  };

  // Portal to <body> so the fixed overlay anchors to the viewport rather than the
  // Payments page container's backdrop-filter (which would centre it mid-page).
  // [FLUID-3 2026-10-12] Inert while it closes: the form stays on screen for the 0.18 s exit, and a key press
  // in it then (Enter held down after Add) must not submit the payment a second time.
  return createPortal(
    <Overlay onClick={onClose} $open={open} inert={closing}>
      <Modal onClick={(e) => e.stopPropagation()} $open={open}>
        <Form onSubmit={handleSubmit}>
          <Header>
            <Title>Add Payment</Title>
            <CloseBtn type="button" onClick={onClose}>×</CloseBtn>
          </Header>
          <Body>
            {error && <ErrorText>{error}</ErrorText>}

            <Field>
              <FieldLabel>Payment Date <Required>*</Required></FieldLabel>
              {/* [DATE-STYLE 2026-09-29, user] Reads "Sep 29, 2026"; defaults to today; the save still
                  refuses an empty date ("Payment date is required"). */}
              <DateCell>
                <SingleDateCalendar value={form.payment_date} onChange={(d) => set('payment_date', d)} />
              </DateCell>
            </Field>

            <Field>
              <FieldLabel>Account <Required>*</Required></FieldLabel>
              <Select liquidList value={form.account_name} onChange={(e) => set('account_name', e.target.value)} required>
                {accounts.length === 0 && <option value="">— No accounts loaded —</option>}
                {accounts.map((a) => <option key={a} value={a}>{a}</option>)}
              </Select>
            </Field>

            <Field>
              <FieldLabel>Charge ID <Required>*</Required></FieldLabel>
              <Input type="text" value={form.charge_id} onChange={(e) => set('charge_id', e.target.value)} placeholder="e.g. ch_3PAbc... or MANUAL-001" required />
            </Field>

            <Field>
              <FieldLabel>Amount ($) <Required>*</Required></FieldLabel>
              <Input type="number" step="0.01" min="0" value={form.amount as any} onChange={(e) => set('amount', e.target.value)} placeholder="0.00" required />
            </Field>

            <Field>
              <FieldLabel>Email</FieldLabel>
              <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="client@example.com" />
            </Field>

            <Field>
              <FieldLabel>Website</FieldLabel>
              <Input type="text" value={form.website} onChange={(e) => set('website', e.target.value)} placeholder="example.com" />
            </Field>

            <Field>
              <FieldLabel>Client Name</FieldLabel>
              <Input type="text" value={form.client_name} onChange={(e) => set('client_name', e.target.value)} placeholder="John Doe" />
            </Field>

            <Field>
              <FieldLabel>Object Type</FieldLabel>
              <Input type="text" value={form.object_type} onChange={(e) => set('object_type', e.target.value)} placeholder="charge / payment" />
            </Field>

            <Field>
              <FieldLabel>Invoice Number</FieldLabel>
              <Input type="text" value={form.invoice_number} onChange={(e) => set('invoice_number', e.target.value)} placeholder="INV-..." />
            </Field>

            <Field>
              <FieldLabel>Status</FieldLabel>
              <Input type="text" value={form.status} onChange={(e) => set('status', e.target.value)} placeholder="succeeded" />
            </Field>

            <Field $full>
              <FieldLabel>Description</FieldLabel>
              <Input type="text" value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Optional notes" />
            </Field>
          </Body>
          <Footer>
            <Btn type="button" onClick={onClose} disabled={saving}>Cancel</Btn>
            <Btn $primary type="submit" disabled={saving}>{saving ? 'Saving…' : 'Add Payment'}</Btn>
          </Footer>
        </Form>
      </Modal>
    </Overlay>,
    document.body
  );
};

export default AddPaymentModal;
